import React from 'react';

import { customsInstructionApis } from '@apis/next/customsInstructionApis';
import gotoIcon from '@assets/img/icons/goto.svg';
import tickCircleSvg from '@assets/img/icons/tick-circle.svg';
import { HorizontalSpace } from '@components/HorizontalSpace';
import { isInstructionOverdue } from '@components/utilities/Bookings/helpers';
import { calendarStrings } from '@data/calendar';
import { getInstructionStatusMeta, instructionStrings } from '@data/instruction';
import { useDeliveryInstruction } from '@hooks/queries/useDeliveryInstruction';
import { useUser } from '@hooks/queries/useUser';
import { ClassMap, makeMuiStyles, useCommonStyles } from '@styles';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import clsx from 'clsx';
import Color from 'color';
import dayjs from 'dayjs';
import { useLocation } from 'react-router-dom';

const strings = createStrings({
  ja: {
    deadline: '締め切り',
    overdue: '期間切れ',
    before: (year: number, month: number, date: number, time: string) =>
      `${year}年${month}月${date}日 ${time}まで`,
    pleaseComplete: (formName: string) => `${formName} を記入してください`,
    promptDeliveryInstruction: 'ご指示が遅れると、最短納品日に影響が出る可能性がございます。',
    promptCustomsInstruction: '資料を入手出来次第、お早めにアップロードしてください。',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  mainContainer: {
    display: 'grid',
    columnGap: '1.25rem',
    gridTemplateColumns: '1fr 1fr',
    margin: '1.875rem 0 3.5rem 0',
    [theme.breakpoints.down('sm')]: {
      columnGap: '0',
      rowGap: '1.25rem',
      gridTemplateColumns: '1fr',
    },
  },
  item: {
    ...common.middleText,
    ...common.flexInline,
    ...common.justifyBetween,
    ...common.baseButton,
    ...common.textPrimary,
    padding: '1.25rem 1.125rem 1.125rem 1.25rem',
    borderRadius: 8,
    borderWidth: '2px',
    borderStyle: 'solid',
    backgroundColor: Color(theme.palette.primary.main).alpha(0.1).toString(),
    borderColor: theme.palette.primary.main,
    '&$error': {
      ...common.textError,
      backgroundColor: Color(theme.palette.error.main).alpha(0.1).toString(),
      borderColor: theme.palette.error.main,
    },
  },
  textContainer: {
    ...common.flexColumn,
    alignItems: 'start',
    minWidth: '16.875rem',
  },
  checkIcon: {
    position: 'relative',
    marginRight: '10px',
  },
  gotoIcon: {
    filter:
      'brightness(0) saturate(100%) invert(22%) sepia(81%) saturate(994%) hue-rotate(159deg) brightness(95%) contrast(102%)',
    '$similar &': {
      filter:
        'brightness(0) saturate(100%) invert(20%) sepia(49%) saturate(2650%) hue-rotate(190deg) brightness(88%) contrast(102%)',
    },
    '$error &': {
      filter:
        'brightness(0) saturate(100%) invert(23%) sepia(79%) saturate(1293%) hue-rotate(343deg) brightness(100%) contrast(103%)',
    },
  },
  error: {},
  similar: {},
}));

export interface InstructionFormEntranceProps {
  text: string;
  url: string;
  deadline: string;
  sent: boolean;
  classes?: ClassMap<'item'>;
  color?: 'primary';
  prompt: string;
}

export const InstructionFormEntrance: React.FC<InstructionFormEntranceProps> = (props) => {
  const { text, url, deadline, sent, classes: customClasses, prompt } = props;

  const classes = { ...useStyles(), ...useCommonStyles() };

  const isOverdue = !sent && isInstructionOverdue(deadline);

  return (
    <div
      className={clsx(classes.item, isOverdue && classes.error, customClasses?.item)}
      onClick={() => {
        if (window.getSelection()?.toString()) {
          return;
        }
        window.open(url, '_blank');
      }}
    >
      <div className={classes.textContainer}>
        <div className={classes.flexInline}>
          {sent && (
            <img
              className={classes.checkIcon}
              src={tickCircleSvg}
              width={20}
              height={20}
              alt="tick"
            />
          )}
          <div className={clsx(classes.middleText, classes.fontWeight700)}>{text}</div>
        </div>
        <div className={classes.smallerText}>
          {strings.deadline}　{calendarStrings.yearMonthDay(dayjs(deadline).toDate())}
          {` 16:00`}
          {formStrings.until}
          {isOverdue && `（${strings.overdue}）`}
        </div>
        {!sent && (
          <div className={clsx(classes.smallerText, classes.fontWeight700Important)}>{prompt}</div>
        )}
      </div>
      <HorizontalSpace width={14} />
      <img src={gotoIcon} className={classes.gotoIcon} width={25} height={25} alt="goto" />
    </div>
  );
};

export interface InstructionFormEntrancesProps {
  d2dNo: string;
  classes?: ClassMap<'root'>;
}

export const InstructionFormEntrances: React.FC<InstructionFormEntrancesProps> = (props) => {
  const { d2dNo, classes: customClasses } = props;
  const classes = { ...useStyles(), ...useCommonStyles() };
  const location = useLocation();
  const fromUrl = encodeURIComponent(`${location.pathname}${location.search}`);
  const user = useUser();

  const customsInstruction = customsInstructionApis.useGet('/customs-instruction/:d2d_no', {
    params: {
      d2d_no: d2dNo,
    },
  });
  const deliveryInstruction = useDeliveryInstruction(d2dNo);

  if (!customsInstruction.data || !deliveryInstruction.data || !user.data) {
    return null;
  }

  const customsCurrentStatus =
    customsInstruction.data.status[customsInstruction.data?.status.length - 1];
  const deliveryCurrentStatus =
    deliveryInstruction.data.status[deliveryInstruction.data?.status.length - 1];

  const isCustomsSent = getInstructionStatusMeta(customsCurrentStatus.name).isSent;
  const isDeliverySent = getInstructionStatusMeta(deliveryCurrentStatus.name).isSent;

  return (
    <div className={clsx(classes.mainContainer, customClasses?.root)}>
      <InstructionFormEntrance
        text={
          isCustomsSent
            ? `${instructionStrings.customsInstruction}-${d2dNo}`
            : strings.pleaseComplete(`${instructionStrings.customsInstruction}-${d2dNo}`)
        }
        url={`${url('/utilities/Bookings/CustomsInstruction')}?d2dNo=${d2dNo}&from=${fromUrl}`}
        deadline={customsInstruction.data?.cutoff_date!}
        sent={isCustomsSent}
        prompt={strings.promptCustomsInstruction}
      />

      <InstructionFormEntrance
        text={
          isDeliverySent
            ? `${instructionStrings.deliveryInstruction}-${d2dNo}`
            : strings.pleaseComplete(`${instructionStrings.deliveryInstruction}-${d2dNo}`)
        }
        url={`${url('/utilities/Bookings/DeliveryInstruction')}?d2dNo=${d2dNo}&from=${fromUrl}`}
        deadline={customsInstruction.data?.cutoff_date!}
        sent={isDeliverySent}
        prompt={strings.promptDeliveryInstruction}
      />
    </div>
  );
};
